import React, { useMemo } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import AdminTableHeaderWithCourseFilter from "../../components/adminDashboard/AdminTable/AdminTableHeaderWithCourseFilter";
import CourseAnalyticsTable from "../../components/adminDashboard/Analytics/CourseAnalyticsTable";
import CustomText from "../../components/common/general/CustomText/CustomText";

const COURSE_SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Total Sessions (High - Low)", value: "sessions_desc" },
  { label: "Total Sessions (Low - High)", value: "sessions_asc" },
  { label: "Total Duration (High - Low)", value: "duration_desc" },
  { label: "Total Duration (Low - High)", value: "duration_asc" },
  { label: "Avg Duration (High - Low)", value: "avg_duration_desc" },
  { label: "Avg Duration (Low - High)", value: "avg_duration_asc" },
];

interface CourseAnalyticsSectionProps {
  coursesData: any;
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  sortDropdownOpen: boolean;
  setSortDropdownOpen: (open: boolean) => void;
}

const CourseAnalyticsSection: React.FC<CourseAnalyticsSectionProps> = ({
  coursesData,
  loading,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortDropdownOpen,
  setSortDropdownOpen,
}) => {
  const processedCourses = useMemo(() => {
    if (!coursesData?.getCourseSessionAnalytics?.data) return [];

    const filteredCourses = coursesData.getCourseSessionAnalytics.data.filter((course: any) => {
      if (!searchQuery) return true;
      return course.name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    filteredCourses.sort((a: any, b: any) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "sessions_desc":
          return b.totalSessions - a.totalSessions;
        case "sessions_asc":
          return a.totalSessions - b.totalSessions;
        case "duration_desc":
          return b.totalDuration - a.totalDuration;
        case "duration_asc":
          return a.totalDuration - b.totalDuration;
        case "avg_duration_desc":
          return b.averageDuration - a.averageDuration;
        case "avg_duration_asc":
          return a.averageDuration - b.averageDuration;
        default:
          return 0;
      }
    });

    return filteredCourses;
  }, [coursesData, searchQuery, sortBy]);

  return (
    <>
      <AdminTableHeaderWithCourseFilter
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDropdownOpen={sortDropdownOpen}
        setSortDropdownOpen={setSortDropdownOpen}
        sortItems={COURSE_SORT_ITEMS}
        coursesOnly={false}
        setCoursesOnly={() => {}}
        availableCourses={[]}
        selectedCourses={[]}
        setSelectedCourses={() => {}}
        showCourseFilter={false}
      />
      {loading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0084FF" />
        </View>
      ) : processedCourses.length > 0 ? (
        <CourseAnalyticsTable courses={processedCourses as any[]} />
      ) : (
        <View style={styles.emptyState}>
          <CustomText text="No courses found." />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
});

export default CourseAnalyticsSection;
